import { ActionButton } from "@octopusdeploy/design-system-components";
import { StringHelper } from "@octopusdeploy/design-system-octopus-components";
import type { MachineModelHealthStatus, OctopusServerNodeSummaryResource } from "@octopusdeploy/octopus-server-client";
import { HostingEnvironment, MachineModelHealthStatusValues } from "@octopusdeploy/octopus-server-client";
import type { LinkHref, Url } from "@octopusdeploy/portal-routes";
import { load } from "js-yaml";
import { cloneDeep } from "lodash";
import moment from "moment/moment";
import type { ReactNode } from "react";
import { useEffect } from "react";
import URI from "urijs";
import type { AnalyticGuidedSetupDispatched, AnalyticKubernetesAgentSetupDispatcher } from "~/analytics/Analytics";
import type { EndpointCardDialogProps } from "~/areas/infrastructure/components/MachineSettings/Endpoints/EndpointCard";
import { generateBearerToken } from "~/areas/infrastructure/components/MachineSettings/Endpoints/KubernetesAgent/BearerTokenGenerator";
import { ConfigurationMessages } from "~/areas/infrastructure/components/MachineSettings/Endpoints/KubernetesAgent/ConfigurationMessages";
import { HaNodeConfigurationPage } from "~/areas/infrastructure/components/MachineSettings/Endpoints/KubernetesAgent/HaNodeConfigurationPage";
import { NfsDriverInstallationPage } from "~/areas/infrastructure/components/MachineSettings/Endpoints/KubernetesAgent/NfsDriverInstallationPage";
import { DefaultPermissionsPage } from "~/areas/infrastructure/components/MachineSettings/Endpoints/KubernetesAgent/OctopusPermissionsController/DefaultPermissionsPage";
import { getBackendServerUrl, getServerGrpcUri, type BaseConfiguration } from "~/areas/infrastructure/components/MachineSettings/Endpoints/KubernetesAgent/configurationUtils";
import { isRoleRulesWrapper } from "~/areas/infrastructure/components/MachineSettings/Endpoints/KubernetesAgent/createHelmCommand";
import { EndpointSelectionScope } from "~/areas/infrastructure/components/MachineSettings/Endpoints/endpointRegistry";
import { repository } from "~/clientInstance";
import type { FormBaseComponentState } from "~/components/FormBaseComponent/FormBaseComponent";
import { FormBaseComponent } from "~/components/FormBaseComponent/FormBaseComponent";
import InternalRedirect from "~/components/Navigation/InternalRedirect/InternalRedirect";
import type { SpaceAwareNavigation } from "~/components/Navigation/SpaceAwareNavigation/SpaceAwareNavigation";
import type { HelpPanelImage } from "~/components/OnboardingDialog/HelpPanel";
import type { PagedOnboardingDialogPageData, PageNavigation } from "~/components/OnboardingDialog/PagedOnboardingDialog";
import { PagedOnboardingDialog } from "~/components/OnboardingDialog/PagedOnboardingDialog";
import { DefaultPermissionsHelpContent, defaultPermissionsHelpImage, OctopusPermissionsControllerHelpContent, octopusPermissionsControllerHelpImage } from "./ConfigurationDialogHelp";
import { OctopusPermissionsControllerInstallationPage } from "./OctopusPermissionsController/OctopusPermissionsControllerInstallationPage";
interface ProjectSetupProps {
    guidedSetup?: boolean;
    navigate?: SpaceAwareNavigation;
}
export type ConfigurationProps<T extends BaseConfiguration = BaseConfiguration> = EndpointCardDialogProps & ProjectSetupProps & {
    onTargetCompleted?: (model: T) => void;
    onEstablishingConnection?: (model: T) => void;
    onBearerTokenExpired?: () => void;
    dispatchAction: AnalyticKubernetesAgentSetupDispatcher | AnalyticGuidedSetupDispatched;
    spaceId: string;
};
export type FieldErrors = {
    [key: string]: string;
};
export type NameConfigurationMessages = {
    HelperText: string;
    Validation: {
        NotProvided: string;
        AlreadyExists: string;
    };
};
export interface ServerNodeCommsAddress {
    nodeName: string;
    commsAddress: string;
}
export interface ConfigurationState<Configuration extends BaseConfiguration> extends FormBaseComponentState<Configuration> {
    canModifyServerUrls: boolean;
    advancedVisited: boolean;
    isBusy: boolean;
    redirectTo?: LinkHref;
    hasTokenExpired: boolean;
    accessTokenExpiryCheckIntervalId: ReturnType<typeof setTimeout> | undefined;
    accessTokenExpiry: moment.Moment | undefined;
    usePersistentVolumeStorage: boolean;
    useOctopusPermissionsController: boolean;
    requiresHaConfiguration: boolean;
}
export abstract class ConfigurationDialog<State extends ConfigurationState<ModelState>, ModelState extends BaseConfiguration, Props extends ConfigurationProps<ModelState> = ConfigurationProps<ModelState>> extends FormBaseComponent<Props, State, ModelState> {
    protected constructor(props: Props, machineType: EndpointSelectionScope, nameErrorMessages: NameConfigurationMessages) {
        super(props);
        this.machineType = machineType;
        this.nameErrorMessages = nameErrorMessages;
        this.initialiseState();
    }
    nameErrorMessages: NameConfigurationMessages;
    machineType: EndpointSelectionScope;
    defaultCommsPort = "10943"; // see https://octopus.com/docs/infrastructure/deployment-targets/tentacle/tentacle-communication#polling-tentacles
    defaultGrpcPort = "8443";
    cloudHostedCommsPort = ""; // see https://octopus.com/docs/infrastructure/deployment-targets/tentacle/polling-tentacles-over-port-443#octopus-cloud
    cloudHostedSubdomain = "polling";
    protected getInitialBaseModel(): BaseConfiguration {
        return {
            SpaceId: this.props.spaceId,
            Space: undefined,
            Name: "",
            ServerUri: getBackendServerUrl(),
            ServerNodeCommsAddresses: [],
            AccessToken: "",
            StorageClassName: "",
            ServerGrpcUri: getServerGrpcUri(),
            DefaultClusterRolePermissions: ``,
        };
    }
    protected initialBaseState: ConfigurationState<ModelState> = {
        canModifyServerUrls: false,
        advancedVisited: false,
        model: this.initialModel(),
        cleanModel: this.initialModel(),
        isBusy: false,
        redirectTo: undefined,
        hasTokenExpired: false,
        accessTokenExpiryCheckIntervalId: undefined,
        accessTokenExpiry: undefined,
        usePersistentVolumeStorage: false,
        requiresHaConfiguration: false,
        useOctopusPermissionsController: false,
    };
    abstract initialModel(): ModelState;
    abstract initialiseState(): void;
    abstract loadMachineTypeSpecificData(): Promise<void>;
    abstract machineIsFound(): boolean;
    abstract machineHealthStatus(): MachineModelHealthStatus | undefined;
    abstract getLinkToMachine(): Url | undefined;
    abstract getPages(): PagedOnboardingDialogPageData[];
    // The index of the first conditional page (i.e. the first page after the Basic and Advanced configuration pages)
    abstract getFirstConditionalPageIndex(): number;
    nodeIsUiOnly = (node: OctopusServerNodeSummaryResource): boolean => {
        return node.MaxConcurrentTasks === 0;
    };
    nodeIsConsideredOnline = (node: OctopusServerNodeSummaryResource): boolean => {
        return !node.IsOffline || moment(node.LastSeen) > moment().subtract(5, "days");
    };
    async componentDidMount() {
        await this.doBusyTask(async () => {
            const spacePromise = repository.Spaces.get(this.props.spaceId);
            const licensePromise = repository.Licenses.getCurrentStatus();
            const serverNodesSummary = await repository.OctopusServerNodes.summary();
            const serverNodes = serverNodesSummary.Nodes.filter((node) => {
                return !this.nodeIsUiOnly(node) && this.nodeIsConsideredOnline(node);
            });
            const isSelfHosted = (await licensePromise).HostingEnvironment === HostingEnvironment.SelfHosted;
            const requiresHaConfiguration = isSelfHosted && serverNodes.length > 1;
            const serverNodeCommsAddresses: ServerNodeCommsAddress[] = [];
            if (requiresHaConfiguration) {
                for (const node of serverNodes) {
                    serverNodeCommsAddresses.push({ nodeName: node.Name, commsAddress: "" });
                }
            }
            else {
                const uri = new URI(this.state.model.ServerUri);
                const serverCommsAddress = isSelfHosted ? uri.port(this.defaultCommsPort).toString() : uri.subdomain(`${this.cloudHostedSubdomain}.${uri.subdomain()}`).port(this.cloudHostedCommsPort).toString();
                serverNodeCommsAddresses.push({ nodeName: "", commsAddress: serverCommsAddress });
            }
            this.setBaseConfiguration({
                Space: await spacePromise,
                ServerNodeCommsAddresses: serverNodeCommsAddresses,
            });
            this.setState({
                canModifyServerUrls: isSelfHosted,
                cleanModel: cloneDeep(this.state.model),
                requiresHaConfiguration,
            });
            await this.loadMachineTypeSpecificData();
        }, {});
        const pollingIntervalMs = 5000;
        const accessTokenExpiryCheckIntervalId: ReturnType<typeof setTimeout> = setInterval(async () => {
            const hasTokenExpired = this.state.accessTokenExpiry != undefined && this.state.accessTokenExpiry < moment();
            if (this.state.hasTokenExpired !== hasTokenExpired) {
                this.props.onBearerTokenExpired?.();
                this.setState({ hasTokenExpired });
            }
        }, pollingIntervalMs);
        this.setState({ accessTokenExpiryCheckIntervalId });
    }
    setBaseConfiguration<K extends keyof BaseConfiguration>(configuration: Pick<BaseConfiguration, K>) {
        this.setModelState({ ...this.state.model, ...configuration });
    }
    componentWillUnmount() {
        clearInterval(this.state.accessTokenExpiryCheckIntervalId);
        super.componentWillUnmount();
    }
    isValidUrl = (url: string): boolean => {
        try {
            const uri = new URI(url);
            return uri.is("absolute") && uri.is("url") && (uri.is("name") || uri.is("ip"));
        }
        catch (error) {
            return false;
        }
    };
    isHaConfigValid = async () => {
        this.clearErrors();
        const previousCommsAddresses: string[] = [];
        const fieldErrors: {
            [p: string]: string;
        } = {};
        for (const { nodeName, commsAddress } of this.state.model.ServerNodeCommsAddresses) {
            if (StringHelper.isNullOrWhiteSpace(commsAddress)) {
                fieldErrors[nodeName] = ConfigurationMessages.ServerCommsAddresses.Validation.NotProvided;
            }
            else if (!this.isValidUrl(commsAddress)) {
                fieldErrors[nodeName] = ConfigurationMessages.ServerCommsAddresses.Validation.Invalid;
            }
            else if (previousCommsAddresses.includes(commsAddress)) {
                fieldErrors[nodeName] = ConfigurationMessages.ServerCommsAddresses.Validation.NotUnique;
            }
            previousCommsAddresses.push(commsAddress);
        }
        if (Object.keys(fieldErrors).length > 0) {
            this.setValidationErrors("The following fields have invalid values", fieldErrors);
            return false;
        }
        return true;
    };
    abstract validateMachineTypeSpecificData(model: ModelState, fieldErrors: FieldErrors): Promise<void>;
    validateAdvancedFields = async (): Promise<boolean> => {
        this.clearErrors();
        const fieldErrors: FieldErrors = {};
        const model = this.state.model;
        if (this.state.canModifyServerUrls) {
            if (StringHelper.isNullOrWhiteSpace(model.ServerUri) || !this.isValidUrl(model.ServerUri)) {
                fieldErrors.ServerUri = ConfigurationMessages.ServerUri.Validation.Invalid;
            }
            if (model.ServerNodeCommsAddresses.length === 1) {
                if (StringHelper.isNullOrWhiteSpace(model.ServerNodeCommsAddresses[0].commsAddress) || !this.isValidUrl(model.ServerNodeCommsAddresses[0].commsAddress)) {
                    fieldErrors.ServerNodeCommsAddresses = ConfigurationMessages.ServerCommsAddresses.Validation.Invalid;
                }
            }
        }
        if (Object.keys(fieldErrors).length > 0) {
            this.setValidationErrors("The following fields have invalid values", fieldErrors);
            return false;
        }
        return true;
    };
    validateYamlPermissions = async (): Promise<boolean> => {
        this.clearErrors();
        const fieldErrors: FieldErrors = {};
        const yaml = this.state.model.DefaultClusterRolePermissions;
        if (yaml) {
            try {
                const parsed = load(yaml);
                if (!isRoleRulesWrapper(parsed)) {
                    fieldErrors.DefaultClusterRolePermissions = "YAML must contain a 'rules' array";
                }
            }
            catch (error) {
                fieldErrors.DefaultClusterRolePermissions = `Invalid YAML syntax: ${error instanceof Error ? error.message : "Unknown error"}`;
            }
        }
        if (Object.keys(fieldErrors).length > 0) {
            this.setValidationErrors("The following fields have invalid values", fieldErrors);
            return false;
        }
        return true;
    };
    refreshToken = async () => {
        await this.doBusyTask(async () => {
            this.setState({ isBusy: true });
            this.clearErrors();
            const { token, expiry } = await generateBearerToken();
            this.setModelState({ AccessToken: token.AccessToken });
            this.setState({ accessTokenExpiry: expiry, hasTokenExpired: expiry < moment() });
        });
        this.setState({ isBusy: false });
    };
    clearModel = () => this.setModelState(this.state.cleanModel);
    resetState = () => this.setState({
        advancedVisited: false,
        usePersistentVolumeStorage: false,
        useOctopusPermissionsController: false,
    });
    shouldMoveToFirstPage(currentPageIndex: number, firstConditionalPageIndex: number, advancedVisited: boolean): boolean {
        // We are going to the first page no matter what, run through this to reset the advancedVisited flag
        if (currentPageIndex === 1) {
            return true;
        }
        // If we've visited the advanced page we can stick to the normal flows
        if (advancedVisited) {
            return false;
        }
        // If we're on the first conditional page we can go back to the first page if we never visited advanced
        return currentPageIndex === firstConditionalPageIndex;
    }
    moveToFirstPage(pageNavigation: PageNavigation) {
        this.setState({ advancedVisited: false });
        pageNavigation.moveToFirstPage();
    }
    moveToBasicOrAdvancedPageBackButton(currentPageIndex: number): (pageNavigation: PageNavigation) => ReactNode {
        const onclick = async (pageNavigation: PageNavigation) => this.shouldMoveToFirstPage(currentPageIndex, this.getFirstConditionalPageIndex(), this.state.advancedVisited) ? this.moveToFirstPage(pageNavigation) : pageNavigation.moveToPreviousPage();
        return (pageNavigation: PageNavigation) => <ActionButton label="Back" onClick={() => onclick(pageNavigation)} disabled={!pageNavigation.canMoveToPrevious}/>;
    }
    getNfsCsiDriverInstallationPage(helpPanelContent: JSX.Element, helpPanelImage: HelpPanelImage, backButton: (pageNavigation: PageNavigation) => ReactNode): PagedOnboardingDialogPageData {
        return {
            name: "Install NFS CSI Driver",
            title: "Install NFS CSI Driver",
            progressTrackerLabel: "NFS",
            helpPanelContent,
            helpPanelImage,
            alwaysShowCancelButton: true,
            onMovingToNextPage: async () => {
                this.props.dispatchAction("Submit NFS CSI Screen", {});
            },
            render: () => <NfsDriverInstallationPage />,
            customSecondaryButton: backButton,
            hideBackButton: !!backButton,
        };
    }
    getOctopusPermissionsControllerPage(backButton: (pageNavigation: PageNavigation) => ReactNode): PagedOnboardingDialogPageData {
        return {
            name: "Install Octopus Permissions Controller",
            title: "Install Octopus Permissions Controller",
            progressTrackerLabel: "Permissions",
            helpPanelContent: <OctopusPermissionsControllerHelpContent />,
            helpPanelImage: octopusPermissionsControllerHelpImage(),
            alwaysShowCancelButton: true,
            onMovingToNextPage: async () => {
                this.props.dispatchAction("Submit Octopus Permissions Controller Screen", {});
            },
            render: () => <OctopusPermissionsControllerInstallationPage />,
            customSecondaryButton: backButton,
            hideBackButton: !!backButton,
        };
    }
    getDefaultPermissionsPage(backButton: (pageNavigation: PageNavigation) => ReactNode): PagedOnboardingDialogPageData {
        return {
            name: "Default Permissions for Kubernetes Agent Deployments",
            title: "Default Permissions for Kubernetes Agent Deployments",
            helpPanelContent: <DefaultPermissionsHelpContent />,
            helpPanelImage: defaultPermissionsHelpImage(),
            isPageModelValid: () => this.validateYamlPermissions(),
            alwaysShowCancelButton: true,
            onMovingToNextPage: async () => {
                this.props.dispatchAction("Submit Default Permissions Screen", {});
            },
            render: () => <DefaultPermissionsPage model={this.state.model} onModelChange={(model) => this.setModelState(model)} getFieldError={(name: string) => this.getFieldError(name)} configurePermissions={true}/>,
            customSecondaryButton: backButton,
            hideBackButton: !!backButton,
        };
    }
    getHaConfigurationPage(helpPanelContent: JSX.Element, helpPanelImage: HelpPanelImage, backButton: (pageNavigation: PageNavigation) => ReactNode): PagedOnboardingDialogPageData {
        return {
            name: "Configure High Availability Nodes",
            title: "Configure High Availability Nodes",
            progressTrackerLabel: "HA Nodes",
            helpPanelContent,
            helpPanelImage,
            isPageModelValid: () => this.isHaConfigValid(),
            onMovingToNextPage: async () => {
                this.refreshToken();
                this.props.dispatchAction("Submit Configure High Availability Nodes Screen", {});
            },
            isBusy: this.state.isBusy,
            alwaysShowCancelButton: true,
            render: () => (<HaNodeConfigurationPage serverCommsAddresses={this.state.model.ServerNodeCommsAddresses} onServerCommsAddressesChange={(ServerNodeCommsAddresses) => this.setModelState({ ServerNodeCommsAddresses })} getFieldError={(name: string) => this.getFieldError(name)}/>),
            customSecondaryButton: backButton,
            hideBackButton: !!backButton,
        };
    }
    render() {
        if (this.state.redirectTo) {
            return <InternalRedirect to={this.state.redirectTo} push={false}/>;
        }
        const pages = this.getPages();
        const completeButtonText = this.machineIsFound() ? `View ${this.machineType === EndpointSelectionScope.Worker ? "Worker" : "Deployment Target"}` : undefined;
        return (<>
                {this.props.open && <AnalyticView dispatchAction={this.props.dispatchAction} type={this.machineType === EndpointSelectionScope.Worker ? "Worker" : "Target"}/>}
                <PagedOnboardingDialog open={this.props.open} close={(isCancel) => {
                this.clearModel();
                this.resetState();
                this.clearErrors();
                this.props.closeDialog();
                const redirectTo = this.getLinkToMachine();
                if (redirectTo && !isCancel) {
                    this.setState({ redirectTo });
                    // This workaround is used for cases when the component is hidden behind an 'if' condition
                    // and can't re-render on state update.
                    this.props.navigate?.navigate(redirectTo);
                }
            }} name={"Add Kubernetes Agent"} showProgressTracker={true} completeButtonText={completeButtonText} hidePreviousButton={this.props.guidedSetup ? this.machineHealthStatus() === MachineModelHealthStatusValues.Healthy : this.machineIsFound()} hideCompleteButton={this.props.guidedSetup ? this.machineHealthStatus() !== MachineModelHealthStatusValues.Unhealthy : !this.machineIsFound()} contentPanelHeight={600} pages={pages}/>
            </>);
    }
    static displayName = "ConfigurationDialog";
}
function AnalyticView(props: {
    dispatchAction: AnalyticKubernetesAgentSetupDispatcher | AnalyticGuidedSetupDispatched;
    type: "Worker" | "Target";
}) {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => props.dispatchAction("Click Add Kubernetes " + props.type, {}), []);
    return <></>;
}
